<template>
  <!--
    Full-bleed chat surface. The conversation stays centred; both side
    panels float as fade-in overlays in the flanking whitespace so they
    never push the chat off-centre.
  -->
  <div class="agent-chat-page">
    <!--
      Full-screen splash while Atlas initialises. Covers the whole
      surface so the first impression is a branded moment rather than
      an empty skeleton. Swaying compass + brand-accent copy; first
      visit gets a longer hold so the story has time to land.
    -->
    <transition name="splash-fade">
      <div v-if="status === 'initialising'" class="splash-overlay">
        <div class="splash-compass">
          <AtlasLogo :size="128" variant="brand" />
        </div>
        <p class="splash-eyebrow">Please wait</p>
        <p class="splash-headline">
          Atlas is orienting<span class="splash-dots" aria-hidden="true" />
        </p>
      </div>
    </transition>

    <!-- Main chat area -->
    <div class="chat-area">
      <!-- Header -->
      <div class="header">
        <div class="header-lead">
          <v-btn
            icon
            small
            class="panel-toggle"
            :title="panelOpen ? 'Hide panel' : 'Show panel'"
            @click="panelOpen = !panelOpen"
          >
            <v-icon size="20">{{ panelOpen ? 'mdi-menu-open' : 'mdi-menu' }}</v-icon>
          </v-btn>
          <div class="header-identity">
            <AtlasLogo :size="32" variant="brand" />
            <div class="header-identity-text">
              <!--
                Single-line brand lockup: RoleMapper sits inline with Atlas
                so the full product name reads as one unit. RoleMapper is
                rendered in indigo + medium weight so it sits *alongside*
                the product name rather than dominating it, while Atlas
                takes the bolder primary emphasis.
              -->
              <h1 class="header-title">
                <span class="header-parent-brand">RoleMapper</span>
                <span class="header-product">Atlas</span>
              </h1>
              <!--
                Four brand pillars — each accented in indigo so the
                capitals A-T-L-S visually reinforce the Atlas name.
              -->
              <p class="header-subtitle">
                Your guide through
                <span class="atlas-pillar">Architecture</span>,
                <span class="atlas-pillar">Transparency</span>,
                <span class="atlas-pillar">Levelling</span>
                and
                <span class="atlas-pillar">Skills</span>.
              </p>
            </div>
          </div>
        </div>
        <div class="header-actions">
          <!-- Unified usage pill (conversation size + allowance). The model
               is client policy (atlas_model client setting), resolved
               server-side — not shown in the UI. -->
          <AtlasUsageMeter :usage="contextUsage" :allowance="usageAllowance" />
        </div>
      </div>

    <!-- Messages list -->
    <div ref="scrollerRef" class="scroller">
      <div class="scroller-inner">
        <!-- Messages -->
        <transition-group name="chat-message" tag="div" class="messages-list">
          <ChatMessage
            v-for="message in messages"
            :key="message.id"
            :message="message"
            :can-retry="!!message.isLatestAssistant && status === 'idle'"
            :ready-entry="readyEntryFor(message.chainExecutionId)"
            :doc-creation-in-progress="isDocCreationInProgress(message.chainExecutionId)"
            @ref-click="onRefClick"
            @follow-up="onFollowUp"
            @retry="onRetry"
            :exit-workflow-key="defaultWorkflowKey"
            :parked-instance-id="parkedWorkflow ? parkedWorkflow.instance_id : null"
            @create-document="onCreateDocument"
            @compare-documents="onCompareDocuments"
            @compare-levels="onCompareLevels"
            @open-document="onOpenDocument"
            @open-summary="onOpenSummary"
          />
        </transition-group>

        <!-- Suggestion pills (after greeting, before first user message) -->
        <div
          v-if="messages.length === 1 && status === 'idle'"
          class="suggestions"
        >
          <button
            v-for="suggestion in suggestions"
            :key="suggestion"
            class="suggestion-pill"
            @click="sendMessage(suggestion)"
          >
            {{ suggestion }}
          </button>
        </div>

        <!-- In-flight assistant turn -->
        <transition name="chat-message">
          <div v-if="composing" class="composing">
            <!--
              Identity mark row (DS AtlasChat turn anatomy) — the compass
              sways while the turn is in flight (same motion as the
              initial-load "finding bearings" animation, scaled down).
            -->
            <div class="composing-mark">
              <div class="atlas-live-sway">
                <AtlasLogo :size="18" variant="brand" />
              </div>
              <span class="composing-name">Atlas</span>
            </div>
            <div class="composing-bubble-wrapper">
              <div
                v-if="composing.toolCalls.length > 0"
                class="tool-pills"
              >
                <ToolCallIndicator
                  v-for="(call, i) in composing.toolCalls"
                  :key="i"
                  :call="call"
                />
              </div>

              <!--
                Once text starts arriving on the socket, render Claude's
                partial response in-place so the user sees tokens streaming.
                MarkdownContent re-renders reactively on each delta so
                headings/bullets/refs resolve as Claude writes them. Before
                any text, fall back to the rotating thinking label.
              -->
              <div v-if="streamingText" class="streaming-bubble">
                <MarkdownContent :text="streamingText" @ref-click="onRefClick" />
                <span class="streaming-cursor">▍</span>
              </div>
              <!-- While a prompt chain is live, the progress strip
                   (name, current step, bar, elapsed) stands in for the
                   generic "advance workflow · Ns" thinking label. -->
              <div v-else-if="liveChain" class="thinking-chain">
                <ChainProgressStrip :progress="liveChain" :elapsed-label="liveChainElapsed" />
              </div>
              <div v-else class="thinking-bubble">
                <span class="dots" aria-hidden="true">
                  <span class="dot" style="animation-delay: 0ms" />
                  <span class="dot" style="animation-delay: 160ms" />
                  <span class="dot" style="animation-delay: 320ms" />
                </span>
                <span>{{ thinkingDisplay }}</span>
              </div>
            </div>
          </div>
        </transition>

        <!-- Workflow-owned chains that OUTLIVE their turn (e.g. a
             create-form's content generation, detached from the HTTP
             turn): live progress rendered in the thread — the agents
             panel deliberately skips these. -->
        <div
          v-for="c in inChatChains"
          :key="`inchat-${c.execution_id}`"
          class="inchat-chain"
        >
          <ChainProgressStrip :progress="c" @dismiss="dismissChain" />
        </div>

        <!-- Workflow-owned DOCUMENT CREATIONS (the run after the content
             chain) — same in-thread treatment as inChatChains; the agents
             panel deliberately skips these too. -->
        <div
          v-for="d in inChatDocCreations"
          :key="`inchat-doc-${d.doc_creation_request_id}`"
          class="inchat-chain"
        >
          <DocCreationProgressStrip :progress="d" @dismiss="dismissDocCreation" />
        </div>

        <!-- Handoff filler: a workflow-owned run finished but its
             follow-on (creation kickoff / the created-document card)
             hasn't landed yet — without this the thread shows ~10s of
             dead air after the chain strip completes. Rendered AS a
             strip (synthetic entry) so every status in the thread looks
             the same; pendingHandoff is false while any real strip is
             live, so there is only ever one. -->
        <div v-if="pendingHandoff && !composing" class="inchat-chain">
          <DocCreationProgressStrip :progress="handoffStrip" />
        </div>

        <!-- Error banner -->
        <v-alert
          v-if="error"
          type="error"
          text
          dense
          class="error-banner"
        >
          {{ error }}
        </v-alert>

        <!-- Monthly-allowance banner: amber tinted warn from 80%; at
             100% it goes SOLID red and prominent — the tinted amber→red
             flip alone was too easy to miss for a state that disables
             the whole chat. -->
        <v-alert
          v-if="allowanceNotice"
          :type="isBudgetBlocked ? 'error' : 'warning'"
          :text="!isBudgetBlocked"
          :prominent="isBudgetBlocked"
          dense
          class="error-banner"
        >
          {{ allowanceNotice }}
        </v-alert>
      </div>
    </div>

      <!-- Input -->
      <div class="input-area">
        <div class="input-inner">
          <!-- Parked-workflow chip: a workflow card sits superseded
               up-thread; one click re-renders its current step as a
               fresh card at the bottom (the resume marker). -->
          <div
            v-if="parkedWorkflow && status === 'idle' && !isBudgetBlocked"
            class="resume-workflow"
          >
            <button
              type="button"
              class="resume-workflow__btn"
              title="Bring the workflow's current step back as a fresh card"
              @click="onResumeWorkflow"
            >
              <v-icon size="14" color="primary">mdi-play-circle-outline</v-icon>
              {{ parkedWorkflow.label || 'Resume workflow' }}
            </button>
          </div>
          <!-- Start chip: the used-conversation twin of the
               fresh-conversation auto-start (see AtlasFlyout's copy). -->
          <div
            v-if="!parkedWorkflow && defaultWorkflowKey && canStartContextWorkflow && status === 'idle' && !isBudgetBlocked"
            class="resume-workflow"
          >
            <button
              type="button"
              class="resume-workflow__btn"
              title="Start this page's guided workflow"
              @click="onStartContextWorkflow"
            >
              <v-icon size="14" color="primary">mdi-play-circle-outline</v-icon>
              Start guided workflow
            </button>
          </div>
          <ChatInput
            :disabled="status === 'sending' || status === 'initialising' || isBudgetBlocked"
            placeholder="Ask Atlas to find, compare, or map a role…"
            :uploads="uploads"
            @submit="sendMessage"
            @upload-files="onUploadFiles"
            @remove-upload="removeUpload"
          />
          <!-- Governance line (DS AtlasChat footer, copy verbatim). -->
          <p class="input-hint">
            Atlas reads your architecture; it never changes it without your
            sign-off. Verify before publishing.
          </p>
        </div>
      </div>
    </div> <!-- /chat-area -->

    <!--
      Left overlay: control panel. Floats in the left gutter when the
      header menu button is clicked. Fade-only transition — reads as
      the whitespace filling with content rather than a drawer sliding.
    -->
    <transition name="overlay-fade">
      <div
        v-if="panelOpen"
        class="panel-overlay panel-overlay--left"
      >
        <AgentControlPanel
          class="panel-overlay__content"
          :conversations="conversations"
          :active-conversation-id="conversationId"
          @close="panelOpen = false"
          @new-chat="onNewChat"
          @switch="onSwitchConversation"
          @delete="onDeleteConversation"
        />
      </div>
    </transition>

    <!--
      Right overlay: Atlas Agents. Appears in the right gutter only
      when a chain is actively running. The pointer-events wrapper lets
      pointer-through on empty space so it never blocks the chat.
    -->
    <div class="panel-overlay panel-overlay--right panel-overlay--passthrough">
      <div class="panel-overlay__content panel-overlay__content--passthrough">
        <AgentsPanel
          :visible="agentsPanelOpen"
          :active-chains="panelChains"
          :active-doc-creations="panelDocCreations"
          :ready-to-create="readyToCreateList"
          @jump-to="onJumpToMessage"
          @dismiss-chain="dismissChain"
          @dismiss-doc-creation="dismissDocCreation"
          @run-ready="onCreateDocument"
          @dismiss-ready="dismissReadyToCreate"
          @close="agentsPanelOpen = false"
        />
      </div>
    </div>

    <!--
      Compare modal — opened by ChatMessage's "Compare N documents"
      CTA. Renders side-by-side via the same DocumentCompare component
      the document viewer uses, so the layout is identical regardless
      of entry point.
    -->
    <DocumentCompare
      :show="compareView.show"
      :compareList="compareView.items"
      multiDoc
      showWorkflowActions
      @close="onCompareClose"
    />

    <!--
      Quick-preview popover — same DocViewPopUp surface the grid pages
      use for the chat's `document.summary` action. The full page is
      its own host, so it registers the page action in setup() (which
      also makes ChatMessage / doc-list blocks surface the preview
      affordance) and drives the popover locally. The layer class lifts
      it above the floating side panels while keeping the empty space
      click-through.
    -->
    <DocViewPopUp
      class="summary-popup-layer"
      :doc_id="summaryView.doc_id"
      :trigger="summaryView.trigger"
      @openDocument="(d) => $emit('openDocument', d)"
      @copyDocument="(d) => $emit('copyDocument', d)"
    />

    <!--
      Standalone levelling comparison, opened by the in-chat "Compare levels"
      CTA. Same dialog as the jobs grid hosts; v-if'd on show so it mounts
      fresh (reloads the framework) each open.
    -->
    <v-dialog
      v-model="levelCompareView.show"
      content-class="dc-compare-levels-host"
      style="background-color: white"
      @click:outside="levelCompareView.show = false"
    >
      <v-card>
        <v-card-title>
          Compare levels
          <v-spacer />
          <v-btn icon large @click="levelCompareView.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <LevelCompareStandalone
            v-if="levelCompareView.show"
            :doc-ids="levelCompareView.docIds"
            @openDocument="(d) => $emit('openDocument', d)"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { computed, defineComponent, getCurrentInstance, nextTick, ref, watch } from 'vue';
import ChatMessage from '@/components/agent/ChatMessage.vue';
import ChatInput from '@/components/agent/ChatInput.vue';
import ToolCallIndicator from '@/components/agent/ToolCallIndicator.vue';
import MarkdownContent from '@/components/agent/MarkdownContent.vue';
import AgentsPanel from '@/components/agent/AgentsPanel.vue';
import ChainProgressStrip from '@/components/agent/ChainProgressStrip.vue';
import DocCreationProgressStrip from '@/components/agent/DocCreationProgressStrip.vue';
import AtlasLogo from '@/components/agent/AtlasLogo.vue';
import AgentControlPanel from '@/components/agent/AgentControlPanel.vue';
import AtlasUsageMeter from '@/components/agent/AtlasUsageMeter.vue';
import DocumentCompare from '@/components/document/DocumentCompare.vue';
import LevelCompareStandalone from '@/components/levellingEvaluation/LevelCompareStandalone.vue';
import DocViewPopUp from '@/components/common/DocViewPopUp.vue';
import { agentChat } from '@/composables/agent/agentChat.js';
import { usePageAction, dispatchPageAction, useAtlasFlyout, wirePageActionBridge, takePendingWorkflowStart, pendingWorkflowStart } from '@/composables/agent/atlasFlyout.js';
import { resolveDocReference } from '@/composables/agent/resolveDocReference.js';
import { thinkingStatus } from '@/composables/agent/thinkingStatus.js';
import { cleanStreamingText } from '@rm-ui/agent/agentChat';
import { buildWorkflowStartMessage, buildWorkflowResumeMessage } from '@rm-ui/agent/uiBlockSpec';
import store from '@/store';

// Nav-flavoured prompts — leans into the compass / "find your way"
// metaphor without being cheesy. Verbs are concrete actions Atlas can
// actually take on the first turn.
const SUGGESTIONS = [
  'Show me the shape of my hierarchies',
  'Find roles similar to Product Manager',
  'Chart a career path from engineer to director',
];

const PANEL_STORAGE_KEY = 'rm.agent.panelOpen';

export default defineComponent({
  name: 'vAgentChat',
  components: { ChatMessage, ChatInput, ToolCallIndicator, MarkdownContent, AgentsPanel, ChainProgressStrip, DocCreationProgressStrip, AtlasLogo, AgentControlPanel, AtlasUsageMeter, DocumentCompare, LevelCompareStandalone, DocViewPopUp },
  setup(_props, { emit }) {
    const chat = agentChat();
    // This page runs its OWN chat instance (separate from the flyout
    // singleton), so the agent→page bridge that routes a turn's
    // page_action envelope (e.g. open_document) into the handler
    // registry must be wired here too — the flyout only wires its own.
    wirePageActionBridge(chat);
    // Whether a modal overlay (a document view) owns the Atlas context —
    // consulted by contextWorkflowKey below, because a modal doesn't move
    // the route it opens over.
    const { overlayOwnsContext, setPendingCreation } = useAtlasFlyout();
    // Provenance route for this surface — the full-page Atlas chat is
    // its own route, so a constant is accurate (guided-workflow
    // instances record where they were actioned).
    chat.routeName.value = 'agentChat';
    const {
      messages,
      composing,
      status,
      error,
      sendMessage,
      retryLast,
      reset,
      conversationId,
      conversations,
      switchConversation,
      deleteConversation,
      uploads,
      uploadFile,
      removeUpload,
      contextUsage,
      usageAllowance,
      chainProgress,
      dismissChain,
      docCreationProgress,
      dismissDocCreation,
      createDocumentFromChain,
      readyToCreate,
      dismissReadyToCreate,
    } = chat;

    // ── Context workflow auto-start ─────────────────────────────
    // The full-page Atlas route can declare an `atlas_context_workflow`
    // meta override (Admin → Page Permissions → Meta Admin): a fresh
    // conversation on this page auto-starts that guided workflow via
    // the [workflow:start] marker. Gated on the client's
    // atlas_workflows setting.
    const pageInstance = getCurrentInstance();
    // The route's context workflow (feature-gated) — the auto-start key,
    // and also what the workflow cards' Exit action bounces back to.
    function contextWorkflowKey() {
      // A document modal opens OVER this route — see AtlasFlyout's copy.
      if (overlayOwnsContext.value) return null;
      const workflowKey = pageInstance?.proxy?.$route?.meta?.atlas_context_workflow;
      if (!workflowKey) return null;
      const settings = pageInstance?.proxy?.$loginState?.user?.settings || [];
      const enabled = settings.some(
        (s) => s.setting === 'atlas_workflows' && s.value === 'true',
      );
      return enabled ? workflowKey : null;
    }
    chat.autoStart = () => {
      // A Create-dialog "Generate with Atlas" hand-off outranks the
      // route's default workflow: start the stashed workflow parked on
      // its create step, pre-filled with the dialog's values. One-shot —
      // taking it here consumes it.
      const pending = takePendingWorkflowStart();
      if (pending) {
        return buildWorkflowStartMessage(pending.workflow_key, null, {
          entryStepKey: pending.entry_step_key || undefined,
          initialValues: pending.initial_values || undefined,
        });
      }
      const workflowKey = contextWorkflowKey();
      return workflowKey ? buildWorkflowStartMessage(workflowKey) : null;
    };
    // Same key, second consumer: /agent/init sends it as context_workflow
    // so a fresh conversation opens with the workflow's authored intro
    // message (Admin → Atlas Workflows → Intro message) instead of the
    // generic greeting.
    chat.contextWorkflow = () => contextWorkflowKey();
    const defaultWorkflowKey = computed(() => contextWorkflowKey());
    // The page being mounted = the surface is visible; fire whenever a
    // fresh conversation reaches idle (initial load and "new chat").
    watch(
      [status, conversationId],
      ([s]) => {
        if (s === 'idle') chat.maybeAutoStart();
      },
      { immediate: true },
    );
    // A Create-dialog hand-off arriving while this page is ALREADY
    // mounted (the dialog opens over any page, including this one):
    // the current conversation isn't fresh, so auto-start would never
    // fire — reset into a new conversation, whose idle transition then
    // runs maybeAutoStart → autoStart, which consumes the stash. On a
    // navigation hand-off the stash is set before this page mounts, so
    // this watcher stays quiet and the mount path consumes it.
    watch(pendingWorkflowStart, (pending) => {
      if (pending) reset();
    });

    /**
     * Scroll the conversation to a specific assistant bubble — wired to
     * the "View output" button on a terminal chain chip. Locates the
     * bubble via its `data-message-id` attribute and smooth-scrolls it
     * into the centre of the viewport, with a short highlight pulse.
     */
    function onJumpToMessage(messageId) {
      nextTick(() => {
        const el = document.querySelector(`[data-message-id="${messageId}"]`);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('message-jump-highlight');
        setTimeout(() => {
          el.classList.remove('message-jump-highlight');
        }, 1600);
      });
    }

    /**
     * Flatten the in-flight chain-progress map into an ordered list so
     * we can `v-for` with a stable key. Sorted by execution_id so the
     * order matches the order Claude kicked chains off in.
     */
    const activeChains = computed(() =>
      Object.values(chainProgress.value).sort(
        (a, b) => a.execution_id - b.execution_id,
      ),
    );

    /**
     * The most recent still-running chain execution, if any — while one
     * is live, the composing indicator swaps its "advance workflow"
     * label for the chain progress strip (name, current step, bar), so
     * the user sees WHAT is taking the time right where they're looking.
     */
    const liveChain = computed(() => {
      const running = activeChains.value.filter(
        (e) => !['completed', 'failed', 'awaiting_selection', 'timed_out'].includes(e.status),
      );
      return running.length ? running[running.length - 1] : null;
    });

    /**
     * What the agents panel actually lists: everything EXCEPT chains the
     * chat itself is already showing — the one live in the composing
     * indicator, and inline runs that finished within their turn
     * (flagged dismissOnFinalise; their next card rides the same
     * response, so a panel chip would be pure duplication).
     */
    const panelChains = computed(() =>
      activeChains.value.filter((c) => {
        if (c.dismissOnFinalise === true) return false;
        // Workflow-owned runs render their whole lifecycle in the chat
        // (in-chat strip below the thread) — no panel duplicate.
        if (c.in_chat === true) return false;
        if (
          composing.value &&
          liveChain.value &&
          c.execution_id === liveChain.value.execution_id
        ) {
          return false;
        }
        return true;
      }),
    );

    /**
     * Live workflow-owned chains shown IN the chat (below the thread)
     * once their kickoff turn has returned — e.g. a create-form's
     * content generation, which now runs detached from the HTTP turn.
     * While composing, the composing indicator's strip covers it, so
     * this only renders outside composing. Terminal entries vanish —
     * completion arrives as narration/cards in the thread.
     */
    const inChatChains = computed(() => {
      if (composing.value) return [];
      // Live runs AND dead ends stay visible: a run whose process died
      // (server restart → reaped, or locally timed out by the staleness
      // ticker) posts no narration, so hiding its failed strip would
      // erase the only evidence it ended. Completed runs hide — their
      // card/narration lands in the thread. Dead-end strips carry a
      // Dismiss.
      return activeChains.value.filter(
        (c) =>
          c.in_chat === true &&
          !['completed', 'awaiting_selection'].includes(c.status),
      );
    });

    /**
     * Workflow-owned document creations, in-thread — the run that
     * follows the content chain (chain strip completes, creation takes
     * ~10s, then the created card posts). Same rules as inChatChains.
     */
    const inChatDocCreations = computed(() => {
      if (composing.value) return [];
      // Same dead-end rule as inChatChains: only Complete hides (its
      // created card lands in the thread).
      return activeDocCreations.value.filter(
        (d) => d.in_chat === true && d.status !== 'Complete',
      );
    });

    // Gap filler between a run's success and its follow-on landing —
    // see agentChat.pendingHandoff. Rendered via a synthetic strip
    // entry so it looks identical to the real strips it bridges.
    const pendingHandoff = chat.pendingHandoff;
    const handoffStrip = {
      doc_creation_request_id: 'handoff',
      chain_name: 'Finalising document',
      status: 'Finalising',
      progress: { total: 0, complete: 0, failed: 0, percentage: 0 },
    };

    /**
     * Flattened doc-creation requests for the Document Creation Agent
     * card. Sorted by request id so the UI stacks them in the order
     * the user kicked them off.
     */
    const activeDocCreations = computed(() =>
      Object.values(docCreationProgress.value).sort(
        (a, b) => a.doc_creation_request_id - b.doc_creation_request_id,
      ),
    );

    /**
     * Chat-driven creations (doc_upload confirm) narrate their whole
     * lifecycle in the conversation — the panel lists only the rest.
     */
    const panelDocCreations = computed(() =>
      activeDocCreations.value.filter((d) => d.in_chat !== true),
    );

    /**
     * Flattened "Queued" list: chain executions whose narration landed
     * with a document-formatter shaped output but haven't had a create
     * kicked off yet. Rendered under the Document Creation Agent card
     * so users can Run or Dismiss from the panel without scrolling
     * back to the chat CTA.
     */
    const readyToCreateList = computed(() =>
      Object.values(readyToCreate.value).sort(
        (a, b) => a.execution_id - b.execution_id,
      ),
    );

    /**
     * Per-message lookups that let each chain-narration bubble's inline
     * "Create Document" CTA reflect the same state as the RHS panel's
     * Queued strip. The CTA and the strip both read from these maps, so
     * clicking Run (or Dismiss) on either surface updates the other.
     */
    function readyEntryFor(executionId) {
      if (executionId == null) return null;
      return readyToCreate.value[executionId] ?? null;
    }

    function isDocCreationInProgress(executionId) {
      if (executionId == null) return false;
      return Object.values(docCreationProgress.value).some(
        (d) => d.execution_id === executionId,
      );
    }

    /**
     * Sticky visibility for the right-hand Atlas Agents panel. Flips
     * to true the first time any agent activity appears in this
     * session and stays true until the user explicitly closes it with
     * the ✕ button. Individual chip dismissals don't hide the panel.
     */
    const agentsPanelOpen = ref(false);
    watch(
      () =>
        panelChains.value.length +
        panelDocCreations.value.length +
        readyToCreateList.value.length,
      (total, prev) => {
        if (total > 0 && (prev ?? 0) === 0) {
          agentsPanelOpen.value = true;
        }
      },
    );

    /**
     * Handle the "Create Document" CTA on a chain-narration bubble.
     * Bypasses Claude — direct REST call; progress + narration come
     * back through the existing socket pipeline and drive the new
     * emerald panel card on the right.
     */
    async function onCreateDocument(executionId) {
      try {
        await createDocumentFromChain(executionId);
      } catch (err) {
        // Kickoff failure — surface via console for now; the narration
        // pipeline handles in-flight failures on its own path.
        console.error('[onCreateDocument] kickoff failed', err);
      }
    }


    /**
     * Panel visibility. Closed by default on first visit; preference
     * persisted in localStorage so users get their chosen layout back.
     */
    const panelOpen = ref(loadPanelState());

    watch(panelOpen, (open) => {
      try { localStorage.setItem(PANEL_STORAGE_KEY, open ? '1' : '0'); }
      catch { /* storage unavailable */ }
    });

    function loadPanelState() {
      try { return localStorage.getItem(PANEL_STORAGE_KEY) === '1'; }
      catch { return false; }
    }

    /**
     * Text passed to the streaming markdown renderer. Hides any trailing
     * in-flight ```followups block so the user doesn't briefly see raw JSON
     * at the bottom of the bubble before Claude closes the fence.
     */
    const streamingText = computed(() =>
      composing.value ? cleanStreamingText(composing.value.text) : '',
    );

    function onNewChat() {
      reset();
    }

    async function onSwitchConversation(id) {
      await switchConversation(id);
    }

    async function onDeleteConversation(id) {
      // Bypass confirm for POC — add a dialog later if desired.
      await deleteConversation(id);
    }

    /**
     * Kick off uploads in parallel. The composable keeps per-file state so
     * the input pills render their own spinners / errors independently.
     */
    function onUploadFiles(files) {
      for (const file of files) {
        uploadFile(file);
      }
    }

    // Surface the most recent running tool (socket mode, future). In REST
    // mode toolCalls is empty until the turn completes, so the thinking
    // indicator falls back to time-based rotation.
    const isActive = computed(() => composing.value !== null);
    const mostRecentToolName = computed(() => {
      const calls = (composing.value && composing.value.toolCalls) || [];
      for (let i = calls.length - 1; i >= 0; i--) {
        if (calls[i].status === 'running') return calls[i].name;
      }
      return null;
    });
    const mostRecentToolInput = computed(() => {
      const calls = (composing.value && composing.value.toolCalls) || [];
      for (let i = calls.length - 1; i >= 0; i--) {
        if (calls[i].status === 'running') return calls[i].input;
      }
      return null;
    });
    const { display: thinkingDisplay, elapsedSeconds } = thinkingStatus(
      isActive,
      mostRecentToolName,
      mostRecentToolInput,
    );
    const liveChainElapsed = computed(() =>
      elapsedSeconds.value >= 2 ? `${elapsedSeconds.value}s` : '',
    );

    /**
     * Chip click handler — an RM-xxxxxx chip anywhere in the chat opens
     * the document SUMMARY popover, never a chat lookup: ChatMessage
     * short-circuits chips that already carry a doc_id; the rest land
     * here, where the bare reference is resolved server-side
     * (permission-checked) and the popover anchored at the chip. Only
     * when resolution fails (not found / no view permission) does the
     * click degrade to the conversational "Tell me about…" turn.
     */
    async function onRefClick(refData, event) {
      const isObj = refData && typeof refData === 'object';
      const reference = isObj ? refData.reference : refData;
      if (!reference) return;
      if (isObj && refData.doc_id != null && event) {
        onOpenSummary(event, refData);
        return;
      }
      const doc = await resolveDocReference(reference);
      if (doc && event) {
        onOpenSummary(event, doc);
        return;
      }
      if (status.value === 'sending') return;
      sendMessage(`Tell me about ${reference}`);
    }

    /**
     * Follow-up chip click. Sends the suggested question verbatim (Claude
     * phrases them as the user would). Ignored while a turn is in flight.
     */
    function onFollowUp(question) {
      if (status.value === 'sending') return;
      sendMessage(question);
    }

    /**
     * "Resume workflow" chip (parked workflow — its card superseded
     * up-thread). Sends the resume marker; the engine re-renders the
     * current step as a fresh card at the bottom.
     */
    const parkedWorkflow = chat.parkedWorkflow;
    function onResumeWorkflow() {
      if (status.value === 'sending') return;
      const parked = parkedWorkflow.value;
      if (!parked) return;
      sendMessage(buildWorkflowResumeMessage(parked.instance_id, parked.step_key, parked.label));
    }
    /** "Start guided workflow" chip — the used-conversation twin of the
     * fresh-conversation auto-start. */
    const canStartContextWorkflow = chat.canOfferWorkflowStart;
    function onStartContextWorkflow() {
      if (status.value === 'sending') return;
      const workflowKey = contextWorkflowKey();
      if (!workflowKey) return;
      sendMessage(buildWorkflowStartMessage(workflowKey));
    }

    /**
     * Retry the latest assistant response. Pops the assistant turn from
     * history and re-runs the agent against the user's last question;
     * any attachments on that question are re-attached automatically.
     */
    function onRetry() {
      if (status.value === 'sending') return;
      retryLast();
    }

    /**
     * Open DocumentCompare with the doc_ids surfaced by the assistant
     * (only triggered when the agent's follow-ups invited a compare).
     * One modal instance per page; opening a new comparison overwrites
     * the previous selection — the user wouldn't expect two stacked
     * compare grids.
     */
    const compareView = ref({ show: false, items: [] });
    function onCompareDocuments(items) {
      if (!Array.isArray(items) || items.length < 2) return;
      compareView.value = {
        show: true,
        items: items.map((d) => ({ doc_id: d.doc_id })),
      };
    }
    function onCompareClose() {
      compareView.value = { ...compareView.value, show: false };
    }

    // Register the compare capability like summary/open above: the chat
    // affordances (duplicate-gate Compare buttons, the "Compare N
    // documents" CTA) only render where a `document.compare` handler is
    // registered — on the full page WE are the host, driving the local
    // DocumentCompare modal.
    usePageAction('document.compare', (items) => {
      onCompareDocuments(Array.isArray(items) ? items : []);
    });

    /**
     * Document summary popover. ChatMessage only routes ref-chip and
     * doc-list clicks to `open-summary` when a `document.summary` page
     * action is registered; on the full page WE are the host, so we
     * register here — which both enables the affordance and receives
     * the dispatch. The emit handler goes through dispatchPageAction
     * (same contract as the flyout) so there's a single choke point.
     */
    const summaryView = ref({ doc_id: null, trigger: null });

    usePageAction('document.summary', ({ event, doc } = {}) => {
      if (!doc || doc.doc_id == null || !event) return;
      // Wholesale reassign so DocViewPopUp's `trigger` watcher fires
      // even when the same doc is reopened — Event identity differs
      // per click.
      summaryView.value = { doc_id: Number(doc.doc_id), trigger: event };
    });

    function onOpenSummary(event, doc) {
      dispatchPageAction('document.summary', { event, doc });
    }

    /**
     * Standard document open. Same host-registration pattern as the
     * summary popover: registering `document.open` makes the workflow
     * blocks render their "Open" action as an in-app open instead of
     * a new-tab deep link. The handler bubbles the slim doc ref up to
     * App.vue's router-view `openDocument`, which resolves the full
     * row (permission-checked) and opens the standard document view.
     */
    usePageAction('document.open', ({ doc } = {}) => {
      if (!doc || (doc.reference == null && doc.doc_id == null)) return;
      emit('openDocument', doc);
    });

    async function onOpenDocument(doc) {
      // Atlas may have just ADDED this document's job title to the
      // hierarchy, and the document view resolves its hierarchy-linked
      // fields (Job Title, Job Family, Job Family Group) against the
      // cached tree — opening before the refresh lands renders them as
      // the raw hr_id under "Unclassified". The auto-open path
      // (onDocumentsCreated) already waits; clicking Open on a card is
      // the same situation and was missed.
      await store.dispatch('hierarchies/refresh');
      // A post-creation card carries a note: remember it so the document's
      // own page opens the Atlas panel on that document with the context.
      if (doc?.created_note && doc?.reference) {
        setPendingCreation(doc.reference, doc.created_note);
      }
      dispatchPageAction('document.open', { doc });
    }

    // When the Document Creation Agent finishes successfully with a
    // single document, open it for review straight away — the user just
    // clicked "Create Document", so the review step is the expected next
    // beat. Multi-doc runs skip this (no obvious "the" document); the
    // narration's RM chips cover those.
    chat.onDocumentsCreated = async (docs) => {
      if (!Array.isArray(docs) || docs.length !== 1) return;
      // A guided workflow may have ADDED the job title to the hierarchy
      // on the way in, so the cached tree can be a step behind — the
      // document would then render its raw hr_id under "Unclassified".
      // Every other save path in the portal refreshes the same way.
      await store.dispatch('hierarchies/refresh');
      if (docs[0]?.reference) {
        setPendingCreation(
          docs[0].reference,
          `I've created ${docs[0].reference}${docs[0].doc_name ? ` — ${docs[0].doc_name}` : ''}. ` +
            'Ask me to change any part of it, regenerate a section, or compare it with similar roles.',
        );
      }
      dispatchPageAction('document.open', { doc: docs[0] });
    };

    // Monthly-allowance affordances (only when the client has an Atlas
    // budget): banner text from 80%, input disabled at 100% — the server
    // refuses turns at 100% anyway, this just says so up front. When the
    // conversation is also long (context re-processed every call = higher
    // burn per turn), the warn banner names the cheapest fix.
    const CONTEXT_NUDGE_PCT = 60;
    const isBudgetBlocked = computed(() => !!usageAllowance.value?.blocked);
    const allowanceNotice = computed(() => {
      const a = usageAllowance.value;
      if (!a) return null;
      // a.scope names the binding budget: 'user' = this user's own cap.
      const whose = a.scope === 'user' ? 'your personal' : "this month's";
      if (a.blocked) {
        // EXPLICIT about the consequences: the input and the workflow
        // Resume affordance vanish together with this state, and the
        // amber→red flip alone is easy to miss (user report, 2026-08-26).
        return a.scope === 'user'
          ? 'Atlas is paused for you: your personal monthly usage allowance is fully used. ' +
            'The chat input and workflow Resume stay disabled until it resets next month — ' +
            'or an administrator raises your allowance.'
          : "Atlas is paused: this month's usage allowance is fully used. " +
            'The chat input and workflow Resume stay disabled until the allowance resets ' +
            'next month — or an administrator raises the Atlas budget in client settings.';
      }
      if (a.warn) {
        const ctx = contextUsage.value;
        const ctxPct =
          ctx && ctx.context_window ? (ctx.total_tokens / ctx.context_window) * 100 : 0;
        return ctxPct >= CONTEXT_NUDGE_PCT
          ? `You're approaching ${whose} Atlas usage allowance. Long conversations use it faster — starting a new chat resets the conversation size.`
          : `You're approaching ${whose} Atlas usage allowance.`;
      }
      return null;
    });

    /**
     * "Compare levels" (PD-1211) — same in-chat CTA as the flyout, hosted
     * locally here on the full-page chat. Opens the standalone levelling
     * comparison seeded with the evaluated jobs the CTA passed. `docIds` is
     * reassigned wholesale so LevelCompareStandalone re-mounts fresh each time.
     */
    const levelCompareView = ref({ show: false, docIds: [] });
    function onCompareLevels(items) {
      const docIds = (Array.isArray(items) ? items : [])
        .map((d) => Number(d?.doc_id))
        .filter((id) => Number.isFinite(id) && id > 0);
      if (docIds.length < 2) return;
      levelCompareView.value = { show: true, docIds };
    }

    // Register the handler so the in-chat "Compare levels" CTA appears on this
    // full-page route too — its visibility is gated on a handler existing (same
    // condition as in the flyout). The CTA's emit is handled directly via
    // @compare-levels; this registration also catches an agent-dispatched
    // document.compare-levels action. Gated on the `atlas_compare_levels` client
    // setting so the handler isn't registered — and the CTA never shows — when
    // the feature is disabled for the client.
    const compareLevelsEnabled = !!getCurrentInstance()?.proxy?.$loginState?.user?.settings?.some(
      (s) => s.setting === 'atlas_compare_levels' && s.value === 'true');
    if (compareLevelsEnabled) {
      usePageAction('document.compare-levels', (items) => onCompareLevels(items));
    }

    // Auto-scroll to bottom on new messages or composing updates.
    const scrollerRef = ref(null);
    function scrollToBottom() {
      nextTick(() => {
        const el = scrollerRef.value;
        if (el) el.scrollTop = el.scrollHeight;
      });
    }
    watch(() => messages.value.length, scrollToBottom);
    watch(
      () => composing.value,
      (val) => { if (val) scrollToBottom(); },
      { deep: true },
    );

    return {
      messages,
      composing,
      status,
      error,
      sendMessage,
      reset,
      conversationId,
      conversations,
      uploads,
      contextUsage,
      usageAllowance,
      isBudgetBlocked,
      allowanceNotice,
      panelOpen,
      onNewChat,
      onSwitchConversation,
      onDeleteConversation,
      onUploadFiles,
      removeUpload,
      thinkingDisplay,
      liveChain,
      liveChainElapsed,
      streamingText,
      activeChains,
      panelChains,
      inChatChains,
      inChatDocCreations,
      pendingHandoff,
      handoffStrip,
      chainProgress,
      dismissChain,
      activeDocCreations,
      panelDocCreations,
      docCreationProgress,
      dismissDocCreation,
      readyToCreateList,
      dismissReadyToCreate,
      agentsPanelOpen,
      readyEntryFor,
      isDocCreationInProgress,
      onCreateDocument,
      onJumpToMessage,
      onRefClick,
      onFollowUp,
      parkedWorkflow,
      onResumeWorkflow,
      onRetry,
      compareView,
      onCompareDocuments,
      onCompareClose,
      summaryView,
      onOpenSummary,
      onOpenDocument,
      defaultWorkflowKey,
      canStartContextWorkflow,
      onStartContextWorkflow,
      levelCompareView,
      onCompareLevels,
      scrollerRef,
      suggestions: SUGGESTIONS,
    };
  },
});
</script>

<style scoped>
/*
 * Pin the page to all four sides of the Vuetify `.v-main__wrap` ancestor
 * (which is `position: relative`). `height: 100%` doesn't work inside v-main
 * because v-main uses min-height rather than a resolved height, so the
 * percentage has nothing concrete to resolve against. Absolute positioning
 * gives us a guaranteed fixed viewport-aligned container, and the internal
 * flex column then splits into header / scroll / input bands.
 */
/* Full-page Atlas sits the thread on the prototype's lightest cool
   step (subtle blue-grey, near-white) so the white chrome — header,
   cards, floating composer — reads as separate zones. */
.agent-chat-page {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: #f9fafe;
}

/* Chat column takes the full width; side panels float over the
   flanking whitespace as overlays rather than as siblings that push. */
.chat-area {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* Overlay positioning for left/right panels. Pinned to the page
   edges, occupying the gutters around the centred conversation.
   Hidden below the 1280px viewport where gutters collapse. */
.panel-overlay {
  position: absolute;
  top: 72px;
  bottom: 96px;
  width: 300px;
}
.panel-overlay--left {
  left: 0;
  z-index: 20;
}
.panel-overlay--right {
  right: 12px;
  width: 320px;
  z-index: 10;
}
.panel-overlay--passthrough {
  pointer-events: none;
}
.panel-overlay__content {
  height: 100%;
  border-radius: 0 16px 16px 0;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(79, 70, 229, 0.06);
}
.panel-overlay__content--passthrough {
  /*
   * Stay pointer-transparent at the wrapper level so empty space in the
   * right gutter doesn't intercept clicks that should land on whatever
   * sits underneath (e.g. v-menu dropdowns rendered from the header,
   * which are body-portalled with a z-index above v-main but still
   * occupy the same screen coords as this overlay).
   * AgentsPanel's `aside` re-enables pointer-events on itself so its
   * own UI remains clickable.
   */
  pointer-events: none;
  box-shadow: none;
  border-radius: 0;
}
@media (max-width: 1280px) {
  .panel-overlay {
    display: none;
  }
}

/*
 * Host layer for the document-summary popover. Fills the page so the
 * popover's absolute coords resolve against the chat surface, and sits
 * above the side-panel overlays (z 10/20) — without this the Resizable
 * popup (inline z-index 2) would slide UNDER an open panel. The layer
 * itself is click-through; only the popup re-enables pointer events.
 */
.summary-popup-layer {
  position: absolute;
  inset: 0;
  z-index: 30;
  pointer-events: none;
}
.summary-popup-layer ::v-deep .resizable {
  pointer-events: auto;
}

/* Fade in/out — no translation. */
.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.25s ease;
}
.overlay-fade-enter,
.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

/* Highlight pulse applied to a message when a chain chip's "View output"
   button jumps to it — draws the eye briefly, fades out. */
.message-jump-highlight {
  animation: message-jump-highlight-pulse 1.6s ease-out;
  border-radius: 8px;
}
@keyframes message-jump-highlight-pulse {
  0%   { background-color: rgba(99, 102, 241, 0.12); }
  100% { background-color: transparent; }
}

/* Header */
.header {
  flex-shrink: 0;
  padding: 12px 24px;
  background: white;
  border-bottom: 1px solid #edf0f7;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.header-lead {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
/* Header right cluster: context meter + model selector. */
.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.header-identity {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.header-identity-text {
  line-height: 1.15;
  min-width: 0;
}
.panel-toggle {
  flex-shrink: 0;
  margin: 0 !important;
}

.header-title {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin: 0;
  letter-spacing: -0.01em;
  line-height: 1.15;
}
.header-parent-brand {
  font-size: 18px;
  font-weight: 500;
  color: rgb(79 70 229);
}
.header-product {
  font-size: 18px;
  font-weight: 700;
  color: rgb(17 24 39);
}
.header-subtitle {
  margin: 2px 0 0 0;
  font-size: 12px;
  color: rgb(107 114 128);
}
.atlas-pillar {
  color: rgb(79 70 229);
  font-weight: 500;
}

/* Scroller */
/* Prototype full-page thread: generous top/bottom padding, 42rem
   centred column, roomy 28px turn spacing. */
.scroller {
  flex: 1;
  overflow-y: auto;
  padding: 32px 20px 48px;
}
.scroller-inner {
  max-width: 42rem;
  margin: 0 auto;
}
.messages-list > * + * {
  margin-top: 28px;
}

/*
 * Full-screen splash shown while Atlas initialises. A swaying compass
 * mark + brand-accent copy so the entrance reads as an orientation
 * moment rather than a generic skeleton loader.
 */
.splash-overlay {
  position: absolute;
  inset: 0;
  z-index: 50;
  background: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
/*
 * Splash-screen compass — a physical compass needle hunting for north.
 * Wide ±85° sweep (170° total) with ease-in-out so it pauses at each
 * extreme before swinging the other way. Quick through the middle,
 * deliberate at the ends.
 */
.splash-compass {
  margin-bottom: 24px;
  animation: splash-compass-hunt 2.2s ease-in-out infinite;
  transform-origin: 50% 50%;
}
@keyframes splash-compass-hunt {
  0%, 100% { transform: rotate(-85deg); }
  50%      { transform: rotate(85deg); }
}
.splash-eyebrow {
  margin: 0;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.25em;
  color: rgb(107 114 128);
}
.splash-headline {
  margin: 8px 0 0 0;
  font-size: 20px;
  font-weight: 500;
  color: rgb(31 41 55);
}
.splash-dots::after {
  content: '';
  display: inline-block;
  width: 1.5em;
  text-align: left;
  animation: splash-dots 1.4s steps(4, end) infinite;
}
@keyframes splash-dots {
  0%   { content: ''; }
  25%  { content: '.'; }
  50%  { content: '..'; }
  75%  { content: '...'; }
  100% { content: ''; }
}
.splash-fade-enter-active,
.splash-fade-leave-active {
  transition: opacity 0.5s ease;
}
.splash-fade-enter,
.splash-fade-enter-from,
.splash-fade-leave-to {
  opacity: 0;
}

/* Suggestions — Atlas mint tint on hover ties to the brand. */
.suggestions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  padding-top: 8px;
  margin-top: 12px;
}
/* White chips on the tinted thread (grey-on-grey vanished). */
.suggestion-pill {
  padding: 6px 14px;
  border-radius: 9999px;
  font-size: 14px;
  background: white;
  color: rgb(55 65 81);
  border: 1px solid rgb(229 231 235);
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}
.suggestion-pill:hover {
  background: rgb(238 242 255);
  border-color: rgb(199 210 254);
  color: rgb(67 56 202);
}

/* User bubbles on the tinted thread: the prototype paints "yours" one
   cool step deeper than the thread (atc-userbubble → --v3-canvas) —
   the shared grey-50 bubble would vanish against #F9FAFE. */
.scroller-inner ::v-deep .bubble--user {
  background: #edf0f8;
}

/* Composing turn — mark row + indented body, matching ChatMessage's
   assistant-turn anatomy (DS AtlasChat). */
.composing {
  display: block;
  margin-top: 20px;
}
.composing-mark {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.composing-name {
  font-size: 12px;
  font-weight: 600;
  color: rgb(75 85 99);
  letter-spacing: -0.01em;
}

/*
 * Compass sway while a turn is in flight. Same easing as the initial-load
 * "finding bearings" animation (atlas-orient) so the mark feels
 * continuous between the two states. Slightly faster (4s vs 6s) to read
 * as active navigation rather than booting up.
 */
.atlas-live-sway {
  animation: atlas-orient 4s ease-in-out infinite;
  display: inline-flex;
  transform-origin: 50% 50%;
}
.composing-bubble-wrapper {
  max-width: 42rem;
  padding-left: 26px;
}
.tool-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}
/* Thinking row — plain dots + label under the mark, no bubble. */
.thinking-bubble {
  display: flex;
  align-items: center;
  gap: 12px;
  color: rgb(107 114 128);
  font-size: 14px;
}
/* Live chain strip standing in for the thinking label. */
.thinking-chain {
  width: 22rem;
  max-width: 100%;
}
/* Workflow-owned chain running on after its turn — in-thread strip.
   Indent matches the assistant turn's 26px body indent. */
.inchat-chain {
  width: 22rem;
  max-width: 100%;
  margin: 8px 0 8px 26px;
}
/* Handoff filler between a run finishing and its follow-on landing. */
.inchat-finalising {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-style: italic;
  color: rgb(100 116 139);
}
/* Streaming text renders as plain prose, matching the finished
   assistant-turn treatment — no bubble. */
.streaming-bubble {
  color: rgb(75 85 99);
  font-size: 15px;
  line-height: 1.65;
}
.streaming-cursor {
  display: inline-block;
  margin-left: 2px;
  color: #4F46E5;
  animation: streaming-cursor-blink 1s steps(2) infinite;
}
@keyframes streaming-cursor-blink {
  0%, 50% { opacity: 1; }
  50.01%, 100% { opacity: 0; }
}
/* Thinking dots (DS AtlasChat): pulse upward in sequence; static at
   readable opacity for reduced-motion users. */
.dots { display: flex; gap: 4px; color: rgb(156 163 175); }
.dot {
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: currentColor;
  animation: thinking-pulse 1.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}
@keyframes thinking-pulse {
  0%, 60%, 100% { opacity: 0.25; transform: translateY(0); }
  30% { opacity: 1; transform: translateY(-2px); }
}
@media (prefers-reduced-motion: reduce) {
  .dot { animation: none; opacity: 0.6; }
}

.error-banner { margin-top: 12px; }

/* Input area */
/* Floating composer (prototype atc-composer-foot): no border-top —
   a 30px gradient fades the last messages into the thread bg instead
   of a hard seam, and the composer card floats on its own shadow. */
.input-area {
  flex-shrink: 0;
  position: relative;
  padding: 8px 20px 16px;
}
.input-area::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 30px;
  transform: translateY(-100%);
  background: linear-gradient(to bottom, transparent, #f9fafe);
  pointer-events: none;
}
.input-inner {
  max-width: 42rem;
  margin: 0 auto;
}
/* The shared ChatInput card earns the prototype's floating shadow on
   this page only (the docked flyout keeps its flat bordered look). */
.input-inner ::v-deep .chat-input {
  border-color: rgb(229 231 235);
  box-shadow: 0 6px 22px -10px rgba(16, 24, 40, 0.22);
}
.input-hint {
  margin: 6px 0 0 0;
  font-size: 11px;
  color: rgb(156 163 175);
  text-align: center;
}
/* Parked-workflow chip row, directly above the input. */
.resume-workflow {
  display: flex;
  margin-bottom: 8px;
}
.resume-workflow__btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  border-radius: 9999px;
  border: 1px solid rgb(165 180 252);
  background: rgb(238 242 255);
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  color: rgb(67 56 202);
  cursor: pointer;
  transition: border-color 0.12s ease, background 0.12s ease;
}
.resume-workflow__btn:hover {
  border-color: rgb(99 102 241);
  background: rgb(224 231 255);
}

/* Message entry animation */
.chat-message-enter-active {
  transition: opacity 0.25s ease-out, transform 0.25s ease-out;
}
.chat-message-leave-active {
  transition: opacity 0.15s ease-in;
}
.chat-message-enter,
.chat-message-enter-from {
  opacity: 0;
  transform: translateY(6px);
}
.chat-message-leave-to {
  opacity: 0;
}
</style>

<!--
  Non-scoped: `.v-dialog__content` teleports to <body>, outside this
  component's scoped subtree.

  Stacking order for the "Compare levels" flow:
    Atlas flyout (2400) < this host dialog (2450) < DocumentCompare (2500,
    dc-above-atlas-flyout) opened from inside it.
  The host must NOT share DocumentCompare's 2500: if it did, DocumentCompare's
  scrim — z-index computed dynamically as (max-z + 1) — would land above
  DocumentCompare's own forced-2500 content and grey it out. Pinning the host
  to 2450 keeps the inner dialog's scrim below its content.
-->
<style>
.v-dialog__content.dc-compare-levels-host,
.v-dialog__content:has(.dc-compare-levels-host) {
  z-index: 2450 !important;
}
</style>
